<template>
  <div v-if="questionnaire" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedQuestionnaire?.title"
      link-name="questionnaires-management"
      :is-modified="isModified"
      :mode="mode"
      :is-edit-active="questionnaire.isFromManager"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <QuestionnaireDetails
            v-model="modifiedQuestionnaire"
            :questionnaire="questionnaire"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout">
          </QuestionnaireDetails>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <QuestionnaireDetails
              v-model="modifiedQuestionnaire"
              :questionnaire="questionnaire"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
            </QuestionnaireDetails>
          </PerfectScrollbar>
        </template>
      </template>

      <!-- Tables Section -->
      <template #detailsTitle>
        <div
          class="px-4 py-1.5 text-sm text-gray-500 capitalize border-b-2 w-fit border-theme-500 font-medium">
          {{ $t('questionnaire.questions') }}
        </div>
      </template>

      <template #detailsContent>
        <QuestionnaireDetailsTableQuestions
          v-if="modifiedQuestionnaire?.questions"
          :questions="modifiedQuestionnaire?.questions"
          :questionnaire-id="modifiedQuestionnaire.id"
          :mode="mode"
          :reset-state="resetTablesState"
          :isHorizontalLayout="isHorizontalLayout"
          @delete="loadData"
          @navigate-to-question="onNavigateToQuestion">
        </QuestionnaireDetailsTableQuestions>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deepCopy } from '@/utils/data';
import {
  getQuestionnaireById,
  addOrEditQuestionnaire,
  addOrEditQuestion,
} from '@/services/api/questionnaires';
import { RecordMode } from '@/types/Base';
import { v4 as uuidv4 } from 'uuid';
import useVuelidate from '@vuelidate/core';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import QuestionnaireDetailsTableQuestions from '@/components/questionnaires/QuestionnaireDetailsTableQuestions.vue';
import QuestionnaireDetails from '@/components/questionnaires/QuestionnaireDetails.vue';

import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import { type Question, type Questionnaire } from '@/types/Questionnaires';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const $v = useVuelidate();
const route = useRoute();
const router = useRouter();
const questionnaire = ref<Questionnaire>();
const modifiedQuestionnaire = ref<Questionnaire>();

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');

const resetTablesState = ref(false);

onMounted(async () => {
  await loadData();
});

const parentId = ref<string>();
async function loadData() {
  // check if cloned
  parentId.value = history.state?.parentId;

  if (parentId.value) {
    const res = await getQuestionnaireById(parentId.value as string);
    if (res) {
      questionnaire.value = res as Questionnaire;
      questionnaire.value.id = route.params.id as string;
      questionnaire.value.title = `${questionnaire.value.title} (Copy)`;

      // Keep original questions for now - IDs will be updated after prototype cloning in onSaveChanges
      if (questionnaire.value.questions && questionnaire.value.questions.length > 0) {
        questionnaire.value.questions = questionnaire.value.questions.map((question) => {
          const clonedQuestion = deepCopy(question) as any;
          clonedQuestion.recordMode = RecordMode.Added;
          clonedQuestion.createdDate = new Date().toISOString();
          clonedQuestion.createUserId = undefined;
          clonedQuestion.updateUserId = undefined;
          clonedQuestion.lastUpdate = undefined;
          clonedQuestion.isFromManager = true;
          clonedQuestion.qstQuestionnaire = undefined;
          clonedQuestion.qstList = undefined;
          clonedQuestion.qstListRows = undefined;

          return clonedQuestion;
        });
      }
    } else {
      toastStore.error({ text: $t('messages.errors.failedToLoadQuestionnaire') });
      return;
    }
    mode.value = 'edit';
  } else {
    const res = await getQuestionnaireById(route.params.id as string);
    if (res) {
      questionnaire.value = res as Questionnaire;
      mode.value = 'view';
    } else {
      (questionnaire.value = {
        id: route.params.id as string,
        isFromManager: true,
        isActive: true,
        title: '',
        description: '',
        bindingType: 0,
        sequence: 0,
        effectiveFromDate: '',
        effectiveUpToDate: new Date(new Date().getFullYear(), 11, 31).toISOString(),
        mwQuestionnaireType: '',
        listColumns: 0,
        isStandards: false,
        traderGroupId: '',
      }),
        (mode.value = 'edit');
    }
  }
  modifiedQuestionnaire.value = deepCopy<Questionnaire>(questionnaire.value);
}

const isModified = ref<boolean>(false);

watch(
  () => modifiedQuestionnaire.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(questionnaire.value);
  },
  { deep: true }
);

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!questionnaire.value?.createdDate) {
    window.history.replaceState(
      { ...history.state, parentId: undefined },
      '',
      window.location.href
    );
    router.push('/questionnaires-management');
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  if (!questionnaire.value) return;
  mode.value = 'view';
  confirmModalActive.value = false;
  modifiedQuestionnaire.value = deepCopy<Questionnaire>(questionnaire.value);
  resetTablesState.value = true;
  if (!questionnaire.value?.createdDate || isBackMode.value) {
    window.history.replaceState(
      { ...history.state, parentId: undefined },
      '',
      window.location.href
    );
    router.push('/questionnaires-management');
  }
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  mode.value = 'view';
  const {
    id,
    title,
    description,
    bindingId,
    traderGroupId,
    bindingType,
    effectiveFromDate,
    effectiveUpToDate,
    sequence,
    mwQuestionnaireType,
    assortmentId,
    listColumns,
    isStandards,
    isActive,
    isFromManager,
    qstActionId,
  } = modifiedQuestionnaire?.value || {};

  const data = {
    id,
    title,
    description,
    bindingId,
    traderGroupId,
    bindingType,
    effectiveFromDate,
    effectiveUpToDate,
    sequence,
    mwQuestionnaireType,
    assortmentId,
    listColumns,
    isStandards,
    isActive,
    isFromManager,
    qstActionId,
  } as Questionnaire;
  if (parentId.value && modifiedQuestionnaire.value) {
    // Cloning operation
    const saved = await addOrEditQuestionnaire(data as Questionnaire);
    if (saved && modifiedQuestionnaire.value.questions) {
      let hasFailures = false;

      for (const clonedQuestion of modifiedQuestionnaire.value.questions) {
        // Update question IDs
        clonedQuestion.id = uuidv4();
        if ((clonedQuestion as any).qstQuestionnaireId !== undefined) {
          (clonedQuestion as any).qstQuestionnaireId = id as string;
        }

        // Save the cloned question
        const questionSaved = await addOrEditQuestion(id as string, clonedQuestion as Question);
        if (!questionSaved) {
          hasFailures = true;
        }
      }

      if (hasFailures) {
        toastStore.error({
          text: $t('questionnaire.partialCloneError'),
        });
      } else {
        toastStore.success({
          text: $t('questionnaire.questionnaireClonedSuccessfully'),
        });
      }
      parentId.value = undefined;
      window.history.replaceState(
        { ...history.state, parentId: undefined },
        '',
        window.location.href
      );
      mode.value = 'view';
      await loadData();
      resetTablesState.value = true;
    }
  } else if (isModified.value && modifiedQuestionnaire.value) {
    const saved = await addOrEditQuestionnaire(data as Questionnaire);
    if (saved) {
      await loadData();
      resetTablesState.value = true;
    }
  }
}

const scrollbarId = ref<PerfectScrollbarExpose>();

function onEditMode() {
  mode.value = 'edit';
  resetTablesState.value = false;
}

function onNavigateToQuestion(questionId?: string) {
  if (isModified.value) {
    toastStore.info({
      text: $t('questionnaire.saveBeforeNavigating'),
    });
    return;
  }

  if (questionId) {
    router.push({
      name: 'questions-details',
      params: { id: modifiedQuestionnaire.value?.id, qid: questionId },
    });
  } else {
    router.push({
      name: 'questions-details',
      params: { id: modifiedQuestionnaire.value?.id, qid: uuidv4() },
    });
  }
}
</script>
